import {
  fetchShowVariant,
  Formatter,
  useCMSBlogs,
  useCMSProductionPhoto,
  useMicroserviceShowBrand,
} from "@with-nx/hooks-n-helpers";
import { DesignedCard, ReusableSection, Rule } from "@with-nx/simple-ui/atoms";
import {
  CatalogLayout,
  CatalogLayoutWait,
  GridBlog,
  GridStepScroller,
  ImageGallery,
  ReusableSimpleHeroSection,
  SolutionHelpTypes,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
export default function Page({ resolvedUrl }) {
  const router = useRouter();

  const showBrand = useMicroserviceShowBrand({
    slug: router.query?.brand as string,
    redirect: true,
  });
  const blogs = useCMSBlogs(
    {
      filter: {
        showBrandTags: [showBrand?.data?.id],
      },
    },
    Boolean(showBrand?.data?.id)
  );

  const fallbackSlugs = [
    "getting-started",
    "ensuring-your-success-in-theatre-making",
    "digital-scenery-yes-you-can",
  ];

  const fallback = useCMSBlogs(
    {
      filter: {
        slug: fallbackSlugs,
      },
    },
    true
  );
  const images = useCMSProductionPhoto(
    showBrand?.data?.id
      ? {
          filter: {
            showBrandTags: [showBrand?.data?.id?.toString()],
          },
        }
      : {},
    Boolean(showBrand?.data?.id)
  );

  const [showVariants, _showVariants] = useState([]);

  useEffect(() => {
    if (showBrand.loading) {
      return;
    }

    if (showBrand?.data?.shows?.length === 1) {
      router.push(router.asPath + "/" + showBrand?.data?.shows?.[0].slug);
    }

    const getVariants = async () => {
      const variants = await Promise.all(
        showBrand?.data?.shows.map(async (show) => {
          const showVariant = await fetchShowVariant(`${show.slug}`, {
            "includes[choreoGuide]": "true",
            "includes[scenicProjections]": "true",
          });

          let shouldHideLogo =
            !showVariant?.scenicProjections.length && !showVariant?.choreoGuide;

          if (showVariant?.forceShowLogo) {
            shouldHideLogo = false;
          }

          return {
            slug: showVariant?.slug,
            shouldHideLogo,
          };
        })
      );

      _showVariants(variants);
    };

    getVariants();
  }, [
    router,
    showBrand?.data?.shows,
    showBrand?.data?.shows.length,
    showBrand.loading,
  ]);

  const help: SolutionHelpTypes[] = [];

  help.push("choreography-guides");
  help.push("scenic-projections");
  if (showBrand?.data?.dropsCount > 0) {
    help.push("backdrop-library");
  }

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar
      title={
        showBrand?.data?.metaTitle
          ? showBrand?.data?.metaTitle
          : showBrand?.data?.name
          ? `${showBrand?.data?.name} Theatrical Resources`
          : undefined
      }
      description={
        showBrand?.data?.metaDescription
          ? showBrand?.data?.metaDescription
          : showBrand?.data?.marketingDescription
      }
      resolvedUrl={resolvedUrl}
    >
      <CatalogLayoutWait
        until={[
          !showBrand.loading,
          !blogs.loading,
          !fallback.loading,
          !images.loading,
        ]}
      >
        <ReusableSimpleHeroSection
          image={
            showBrand?.data?.attachments?.find((i) => i.name === "image")?.blob
              ?.signedUrl
          }
          title={showBrand?.data?.name}
          description={showBrand?.data?.marketingDescription}
        />

        {showBrand?.data?.shows.length ? (
          <ReusableSection>
            <div className="container">
              <Row gutter={[36, 18]}>
                {showBrand?.data?.shows?.map((item, index) => {
                  if (!item.public) {
                    return null;
                  }

                  const shouldHideLogo = showVariants[index]?.shouldHideLogo;

                  const itemLogo = shouldHideLogo
                    ? undefined
                    : item.attachments?.find((i) => i.name === "logo")?.blob
                        ?.signedUrl;

                  const itemTitle = item.title;

                  const itemColor =
                    item?.logoColor || showBrand?.data?.logoColor || "#000000";

                  return (
                    <Col xs={24} sm={24} md={12} lg={6} key={item.id}>
                      <DesignedCard
                        size="16x9"
                        outside={{
                          title: item.title,
                        }}
                        inside={{
                          large: itemTitle,
                          image: itemLogo,
                          color: itemColor,
                        }}
                        href={router.asPath + "/" + item.slug}
                      />
                    </Col>
                  );
                })}
              </Row>
            </div>
          </ReusableSection>
        ) : undefined}

        {images.images.length >= 5 ? (
          <ReusableSection>
            <div className="container">
              <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
                {showBrand?.data?.name}{" "}
                <span style={{ color: "var(--primary)" }}>
                  Production Photos
                </span>
              </Rule>
            </div>
            <ImageGallery
              per={3}
              images={images.images.map((image) => ({
                src: Formatter.image(image.image),
                meta: {
                  copyright: image.copyrightClaim,
                  organization: image.organizationName,
                  name: image.name,
                  date: image.productionDate,
                },
              }))}
            />
          </ReusableSection>
        ) : undefined}
        {blogs.blogs.length > 0 || fallback.blogs.length > 0 ? (
          <ReusableSection>
            <div className="container">
              <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
                {showBrand?.data?.name}{" "}
                <span style={{ color: "var(--primary)" }}>Stories</span>
              </Rule>
              <Row gutter={[36, 18]}>
                <GridStepScroller
                  xs={24}
                  sm={24}
                  md={12}
                  lg={8}
                  xl={8}
                  items={[
                    ...(blogs.blogs?.filter(
                      (b) => !fallbackSlugs.includes(b.slug)
                    ) || []),
                    ...(fallback.blogs || []),
                  ].map((blog, b) => {
                    return (
                      <GridBlog
                        key={blog._id}
                        date={blog.date}
                        description={blog.description}
                        image={Formatter.compressed(blog.coverImage, 256, true)}
                        title={blog.title}
                        url={`/blog/${blog.slug}`}
                        transparent={false}
                        properties={{
                          height: "100%",
                        }}
                      />
                    );
                  })}
                />
              </Row>
            </div>
          </ReusableSection>
        ) : undefined}
      </CatalogLayoutWait>
    </CatalogLayout>
  );
}
